import { useMutation, useQueryClient } from '@tanstack/react-query';
import { User } from '@demind-inc/core';

import { accountApi, ErrorRes } from '../api';
import { FirestoreUser } from '../types';

interface UseSignInParams {
  userId?: string;
  newUserInfo?: FirestoreUser;
  serverAuthCode?: string;
  refreshToken?: string;
  tokenExpiryDate?: string;
  accessToken?: string;
  scopes?: string[];
  skipCalendarSync?: boolean;
  authProvider?: string;
  generatedClientType?: 'web' | 'mobile' | 'desktop';
  redirectUri?: string;
  codeVerifier?: string;
}

export const useSignIn = () => {
  const queryClient = useQueryClient();

  const signInMutation = useMutation({
    mutationFn: ({
      userId,
      newUserInfo,
      serverAuthCode,
      refreshToken,
      accessToken,
      tokenExpiryDate,
      scopes,
      skipCalendarSync,
      authProvider,
      generatedClientType,
      redirectUri,
      codeVerifier,
    }: UseSignInParams) => {
      const targetUserId = userId || '';
      return accountApi
        .signIn(targetUserId, true, {
          newUserInfo,
          serverAuthCode,
          accessToken,
          refreshToken,
          tokenExpiryDate,
          isNewTrialFlow: true,
          skipCalendarSync,
          scopes,
          authProvider,
          generatedClientType,
          redirectUri,
          codeVerifier,
        })
        .then(({ data }) => {
          if ('error' in data) {
            throw new Error((data as ErrorRes).error);
          }
          return {
            user: data.user as User,
            refreshToken: data.refreshToken as string,
            accessToken: data.accessToken as string,
          };
        });
    },
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifestack.user'] });
      queryClient.invalidateQueries({ queryKey: ['lifestack.subscriptions'] });
    },
  });

  return {
    signIn: signInMutation.mutateAsync,
    ...signInMutation,
  };
};
